import { useCallback, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useProjectsTableFilters } from '@/contexts/ProjectsTableFiltersContext';
import { useFilterPresets } from '@/hooks/useFilterPresets';
import { isRuleEmpty } from '@/lib/tableFilters';
import { getActiveFilterSummaryRows } from '@/utils/filterRuleSummary';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { LevitatingCard } from '@/components/ui/LevitatingCard/LevitatingCard';
import { SectionTitle } from '@/components/ui/SectionTitle/SectionTitle';
import { Button } from '@/components/ui/Button/Button';
import { CounterBadge } from '@/components/ui/CounterBadge/CounterBadge';
import DeleteIcon from '@mui/icons-material/Delete';
import { CloseIcon, FilterAltIcon, FilterAltOffIcon } from '@/components/ui/Icons';
import '@/components/ui/ContextMenu/ContextMenu.css';
import '@/components/ProjectsTable/TableContextMenu.css';
import './ProjectsFiltersNavbarControl.css';

export function ProjectsFiltersNavbarControl() {
  const location = useLocation();
  const isProjectsRoute = location.pathname === '/' || location.pathname === '/projects';
  const btnRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetsSubmenuAnchor, setPresetsSubmenuAnchor] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    filtersEnabled,
    setFiltersEnabled,
    filters,
    clearAllFilters,
    replaceFilters,
    activeFilterCount,
  } = useProjectsTableFilters();
  const { presets, addPreset, removePreset } = useFilterPresets();

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    cancelSubmenuClose();
    submenuCloseTimerRef.current = setTimeout(() => {
      setPresetsSubmenuAnchor(null);
    }, 120);
  }, [cancelSubmenuClose]);

  const closeMenu = useCallback(() => {
    cancelSubmenuClose();
    setMenuOpen(false);
    setPresetsSubmenuAnchor(null);
  }, [cancelSubmenuClose]);

  const openSaveModal = useCallback(() => {
    setPresetName('');
    setSaveModalOpen(true);
    closeMenu();
  }, [closeMenu]);

  const handleSavePreset = useCallback(() => {
    const cleaned = Object.fromEntries(Object.entries(filters).filter(([, r]) => !isRuleEmpty(r)));
    if (Object.keys(cleaned).length === 0) return;
    const name = presetName.trim();
    if (!name) return;
    addPreset(name, cleaned);
    setSaveModalOpen(false);
    setPresetName('');
  }, [addPreset, filters, presetName]);

  const handleSavePresetSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      handleSavePreset();
    },
    [handleSavePreset]
  );

  const handleCloseSaveModal = useCallback(() => {
    setSaveModalOpen(false);
    setPresetName('');
  }, []);

  const handleToggleMenu = useCallback(() => {
    setMenuOpen((o) => !o);
  }, []);

  const saveModalFilterRows = useMemo(() => getActiveFilterSummaryRows(filters), [filters]);

  if (!isProjectsRoute) return null;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`form__btn btn_s navbar__header-icon-btn projects-filters-navbar__btn ${
          filtersEnabled ? 'form__btn_submit' : 'form__btn_cancel'
        }`}
        onClick={handleToggleMenu}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label={
          hasActiveFilters
            ? `Фильтры таблицы проектов, активных фильтров: ${activeFilterCount}`
            : 'Фильтры таблицы проектов'
        }
        title={
          hasActiveFilters
            ? `Фильтры таблицы проектов — применено: ${activeFilterCount}`
            : 'Фильтры таблицы проектов'
        }
      >
        {filtersEnabled ? <FilterAltIcon size={18} /> : <FilterAltOffIcon size={18} />}
        <CounterBadge
          count={activeFilterCount}
          variant="count"
          size="s"
          className="projects-filters-navbar__filter-badge"
        />
      </button>

      <ContextMenu
        isOpen={menuOpen}
        onClose={closeMenu}
        anchorPosition={null}
        anchorEl={menuOpen ? btnRef.current : null}
        anchorPriorityAxis="y"
        excludeRefs={[btnRef]}
      >
        <ul className="context-menu__list table-context-menu__list">
          <li className="context-menu__list-item">
            <button
              type="button"
              className={`context-menu__action ${
                filtersEnabled
                  ? 'projects-filters-navbar-menu__action-filter-off'
                  : 'projects-filters-navbar-menu__action-filter-on'
              }`}
              onClick={() => {
                setFiltersEnabled(!filtersEnabled);
                closeMenu();
              }}
            >
              <i
                className={`fa-solid ${filtersEnabled ? 'fa-filter-circle-xmark' : 'fa-filter'} projects-filters-navbar-menu__fa`}
                aria-hidden
              />
              <span className="context-menu__action-text">
                {filtersEnabled ? 'Отключить фильтры' : 'Включить фильтры'}
              </span>
            </button>
          </li>
          {hasActiveFilters && (
            <li className="context-menu__list-item">
              <button type="button" className="context-menu__action" onClick={openSaveModal}>
                <i className="fa-solid fa-floppy-disk projects-filters-navbar-menu__fa" aria-hidden />
                <span className="context-menu__action-text">Сохранить набор фильтров…</span>
              </button>
            </li>
          )}
          <li
            className="context-menu__list-item table-context-menu__item_with-sub"
            onMouseEnter={(e) => {
              cancelSubmenuClose();
              setPresetsSubmenuAnchor(e.currentTarget);
            }}
            onMouseLeave={() => scheduleSubmenuClose()}
          >
            <button type="button" className="context-menu__action context-menu__action_with-sub">
              <i className="fa-solid fa-user-tag projects-filters-navbar-menu__fa" aria-hidden />
              <span className="context-menu__action-text">Мои наборы фильтров</span>
              <i
                className="fa-solid fa-chevron-right projects-filters-navbar-menu__fa projects-filters-navbar-menu__fa_chevron"
                aria-hidden
              />
            </button>
            {presetsSubmenuAnchor && (
              <ContextMenuSubmenu
                anchorEl={presetsSubmenuAnchor}
                onMouseEnter={cancelSubmenuClose}
                onMouseLeave={() => {
                  setPresetsSubmenuAnchor(null);
                }}
              >
                <ul className="context-menu__list">
                  {presets.length === 0 ? (
                    <li className="context-menu__list-item">
                      <div className="projects-filters-navbar-menu__empty-text">Нет сохранённых наборов</div>
                    </li>
                  ) : (
                    presets.map((p) => (
                      <li key={p.id} className="context-menu__list-item">
                        <div className="projects-filters-navbar-menu__preset-row">
                          <button
                            type="button"
                            className="context-menu__action projects-filters-navbar-menu__preset-apply"
                            onClick={() => {
                              replaceFilters(p.filters);
                              setFiltersEnabled(true);
                              closeMenu();
                            }}
                          >
                            <span className="context-menu__action-text">{p.name}</span>
                          </button>
                          <button
                            type="button"
                            className="modal__close projects-filters-navbar-menu__preset-remove"
                            aria-label={`Удалить набор «${p.name}»`}
                            title="Удалить набор"
                            onClick={(e) => {
                              e.stopPropagation();
                              removePreset(p.id);
                            }}
                          >
                            <CloseIcon size={20} />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </ContextMenuSubmenu>
            )}
          </li>
          {hasActiveFilters && (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action projects-filters-navbar-menu__action-clear"
                onClick={() => {
                  clearAllFilters();
                  closeMenu();
                }}
              >
                <DeleteIcon sx={{ fontSize: 18, color: 'var(--accent-error)', flexShrink: 0 }} aria-hidden />
                <span className="context-menu__action-text">Очистить все фильтры</span>
              </button>
            </li>
          )}
        </ul>
      </ContextMenu>

      <Modal
        open={saveModalOpen}
        onClose={handleCloseSaveModal}
        title="Сохранить набор фильтров"
        headerVariant="island"
        size="md"
      >
        <form onSubmit={handleSavePresetSubmit} className="form">
          <div
            className="form__row form__row_col"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
          >
            <LevitatingCard className="projects-filters-save-modal__filters-card">
              <SectionTitle>Включённые фильтры</SectionTitle>
              <ul className="projects-filters-save-modal__filters-list" role="list">
                {saveModalFilterRows.map(({ columnId, line }) => (
                  <li key={columnId} className="projects-filters-save-modal__filters-item">
                    {line}
                  </li>
                ))}
              </ul>
            </LevitatingCard>
            <Input
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Название набора"
              fullWidth
              autoFocus
              autoComplete="off"
            />
            <div className="form__footer form__footer_modal">
              <Button variant="outlined" type="button" onClick={handleCloseSaveModal}>
                Отмена
              </Button>
              <Button type="submit" variant="primary" disabled={!presetName.trim()}>
                Сохранить
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
